import "../node_modules/@texthelp/styleguide-global-tasks/dist/styles/main.css";
import { RouterProvider, createBrowserRouter, Navigate, useNavigate } from "react-router-dom";
import { createSpriteSheet } from "./spriteSheetUtils";
import React, { useEffect, Suspense } from "react";
import "@texthelp/styleguide-global-tasks/dist/main";
import svgSpriteSheet from "@texthelp/styleguide-global-tasks/dist/all.svg?url";
import { getTheme } from "./functions/themeService/themeService";
import Login from "./pages/Login";
import Homepage from "./pages/Homepage";
import RootLayout from "./pages/RootLayout";
import LoadingScreen from "./components/loadingScreen/LoadingScreen";
import Welcome from "./pages/Welcome";
import Search from "./pages/Search";
import { askForNotificationPermission } from "./functions/notifications/notificationFunctions";
const TodoPage = React.lazy(() => import('./pages/TodoPage'));
const DueDatePage = React.lazy(() => import('./pages/DueDatePage'));
const CalendarPage = React.lazy(() => import('./pages/CalendarPage'));
import ErrorPage from "./pages/ErrorPage";
import { Capacitor } from '@capacitor/core';
import CalendarLogin from "./pages/CalendarLogin";
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useDispatch } from "react-redux";
import { connectToSignalR, stopSignalRConnection, connectToCalendarSyncHub } from "./functions/syncConnection/syncConnection";
import { getItem } from "./utils/storageHelper";

const setBackgroundColor = async () => {
    await EdgeToEdge.setBackgroundColor({ color: '#ffffff' });
    await StatusBar.setStyle({ style: Style.Light });
};

function App() {
    const dispatch = useDispatch();
    useEffect(() => {
        const initializeApp = async () => {
            const selectedTheme = await getTheme();
            document.body.setAttribute("data-th-theme", selectedTheme);
            createSpriteSheet(svgSpriteSheet);

            if (Capacitor.getPlatform() === 'android') {
                if (selectedTheme === 'light') {
                    EdgeToEdge.setBackgroundColor({ color: '#ffffff' });
                    StatusBar.setStyle({ style: Style.Light });
                }
                else {
                    EdgeToEdge.setBackgroundColor({ color: '#2A3953' });
                    StatusBar.setStyle({ style: Style.Dark });
                }
            }
        };
        initializeApp();
    }, []);

    useEffect(() => {
        // Check if service workers are supported
        const installSW = async () => {
            if ('serviceWorker' in navigator) {
                if (Capacitor.isNativePlatform()) {
                    await askForNotificationPermission();
                    //local service workers cannot be registered in capacitor
                } else {
                    // Ask for notification permission
                    await askForNotificationPermission();

                    // Register the service worker (only in production or when available)
                    if (import.meta.env.MODE === 'production' || import.meta.env.VITE_ENABLE_SW === 'true') {
                        navigator.serviceWorker.register('/sw.js', {
                        scope: '/',
                        updateViaCache: 'none'  // Always check for service worker updates
                    }).then(registration => {
                        console.log('Service worker registered:', registration);

                        // Check for updates every 60 seconds
                        setInterval(() => {
                            registration.update();
                        }, 60000);

                        // Listen for updates
                        registration.addEventListener('updatefound', () => {
                            const newWorker = registration.installing;
                            console.log('New service worker found, installing...');
                            
                            newWorker.addEventListener('statechange', () => {
                                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                    // New service worker installed and old one is still controlling
                                    console.log('New version available! Clearing cache and reloading...');
                                    
                                    // Clear all caches before reloading
                                    caches.keys().then(cacheNames => {
                                        return Promise.all(
                                            cacheNames.map(cacheName => {
                                                console.log('Deleting old cache:', cacheName);
                                                return caches.delete(cacheName);
                                            })
                                        );
                                    }).then(() => {
                                        // Automatically reload without prompting user
                                        window.location.reload(true);
                                    }).catch(error => {
                                        console.error('Error clearing caches:', error);
                                        window.location.reload(true);
                                    });
                                }
                            });
                        });

                        // Handle controller change (when new SW takes over)
                        navigator.serviceWorker.addEventListener('controllerchange', () => {
                            console.log('Service worker controller changed, reloading...');
                            window.location.reload(true);
                        });

                    }).catch(error => {
                        console.error(`Service worker registration failed: ${error.name} - ${error.message}`);
                        
                        // If service worker registration fails, try to clear any existing registrations
                        if ('serviceWorker' in navigator) {
                            navigator.serviceWorker.getRegistrations().then(registrations => {
                                for (let registration of registrations) {
                                    registration.unregister().then(() => {
                                        console.log('Unregistered problematic service worker');
                                    });
                                }
                            });
                        }
                    });
                    } else {
                        console.log('Service worker registration skipped (development mode)');
                    }
                }
            } else {
                console.log('Service workers are not supported in this browser.');
            }

        };
        installSW();
    }, []);
    //useEffect(() => {
    //    const initializeSignalR = async () => {
    //        try {
    //            await connectToSignalR(dispatch);

    //        } catch (e) {
    //            console.log("SignalR Connection Failed:", e);
    //        }
    //    };

    //    initializeSignalR();
    //    return () => {
    //        stopSignalRConnection();
    //    };
    //}, [dispatch]);

    // SignalR initialization moved to RootLayout.js after authentication
    // This ensures the user is authenticated before connecting, preventing
    // the connection from being established without proper user identification

    const router = createBrowserRouter([
        {
            path: "/",
            element: <RootLayout />,
            children: [
                { path: "/Overview", element: <Homepage /> },
                { path: "/Overview/:todoid", element: <Homepage /> },
                {
                    path: "/Todo",
                    element: (
                        <Suspense fallback={<LoadingScreen />}>
                            <TodoPage />
                        </Suspense>
                    ),
                },
                {
                    path: "/Due-dates",
                    element: (
                        <Suspense fallback={<LoadingScreen />}>
                            <DueDatePage />
                        </Suspense>
                    ),
                },
                {
                    path: "/Search", element: (
                        <Suspense fallback={<LoadingScreen />}>
                            <Search />
                        </Suspense>
                    ),
                },
                {
                    path: "/Calendar", element: (
                        <Suspense fallback={<LoadingScreen />}>
                            <CalendarPage />
                        </Suspense>
                    ),
                },
            ],
        },
        {
            path: "/CalendarLogin", element: (
                <Suspense fallback={<LoadingScreen />}>
                    <CalendarLogin />
                </Suspense>
            ) },
        {path : "/Login", element: <Login />},
        { path: "/Welcome", element: <Welcome /> },
        { path: "*", element: <Navigate to="/Error" /> },
        { path: "/Error", element: <ErrorPage /> },
    ]);

    return <RouterProvider router={router} />;
}

export default App;
